import { AuthService } from 'src/app/auth/services/auth.service';
import { Injectable, NgZone, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, filter, map } from 'rxjs';
import { ConflictService } from 'src/app/core/offline/conflict.service';
import { inAngularZone } from 'src/app/core/offline/in-angular-zone';
import { OfflineAvailabilityService } from 'src/app/core/offline/offline-availability.service';
import { OfflineModeService } from 'src/app/core/offline/offline-mode.service';
import { EnqueueInput, OfflineStore } from 'src/app/core/offline/offline-store.service';
import {
  EntityRef,
  OfflineQuotaError,
  OutboxEntry,
  SnapshotRecord,
  SyncedCreateRow,
  SyncedEvent,
  isLocalId,
  newLocalId,
} from 'src/app/core/offline/offline.models';
import { OnlineOutcome, OnlineWriter } from 'src/app/core/offline/online-writer.service';
import { REQUEST_TIMEOUT_MS, SyncEngine } from 'src/app/core/offline/sync-engine.service';
import { syncErrorMessage } from 'src/app/core/services/dataService/sync-error.util';
import {
  COMMENT_MAX_LENGTH,
  CommentTooLongError,
  CommentList,
  TaskCommentResult,
  TaskCreateFields,
  TaskCreatePayload,
  TaskCreated,
  TaskDeleteResult,
  TaskEntities,
  TaskFields,
  TaskFilterInfo,
  TaskJournalView,
  TaskList,
  TaskLoad,
  TaskMergeChoices,
  TaskMergeLoad,
  TaskRef,
  TaskSnapshotData,
  TaskTarget,
  TaskUpdatePayload,
  TaskView,
  TaskWriteResult,
} from './task-offline.models';
import { TASK_CONFLICT_KEYS, taskConflictFields, taskLabel } from './task-outbox-handlers';
import { TaskOverlayContext, fromSnapshotComment, fromSnapshotTask, overlayComments, overlayTasks, taskKeyOf, toCommentView, toTaskView } from './task-overlay';

export const TASK_SAVE_TIMEOUT_MS = 30_000;
/** a photo upload is cut off after this long WITHOUT progress (idle timeout, OnlineWriter step 4 Rev1 W1), not in total */
export const TASK_PHOTO_TIMEOUT_MS = REQUEST_TIMEOUT_MS;

/**
 * E-4 (Security-Gate E2): «Erledigt» carries the time it was captured, so the statistics show the day of the work, not
 * the day of the sync. The backend takes it only from the outbox (checked key + X-Bavox-Outbox) within [now − 7 days,
 * now + 5 min] (capped to now); online the same body goes out and the server time wins. Another status drops the field.
 */
export function withCompletionDate<T extends TaskFields>(fields: T, now: () => string = () => new Date().toISOString()): T {
  if (fields.status === undefined) return fields;
  const { completionDate, ...rest } = fields;
  return (fields.status === 'Finished' ? { ...rest, completionDate: completionDate ?? now() } : rest) as T;
}

interface FailedPhoto {
  file: File;
  message: string;
}

const NO_PHOTOS = { rejectedPhotos: [] as File[], failedPhotos: [] as FailedPhoto[] };

function refOf(projectId: string, taskId: string): EntityRef {
  return isLocalId(taskId) ? { projectId, localId: taskId, serverId: null } : { projectId, localId: null, serverId: taskId };
}

function serverIdOf(body: unknown): string | null {
  const id = (body as { data?: { id?: unknown } } | null)?.data?.id;
  return typeof id === 'string' ? id : null;
}

/**
 * Aufgaben und Werksabnahme offline (spec §2, §5): reads tasks and comments from the server data or the project
 * snapshot with the outbox on top (plan C1), writes offline into the outbox and online with an Idempotency-Key through
 * the same handler build(): a write cut off by the network goes into the outbox with the same key and body (plan D7).
 * Shared-link guests never come here (canWorkOffline, plan D13). Pages keep no own queue (CLAUDE.md «Offline»).
 */
@Injectable({ providedIn: 'root' })
export class TaskOfflineService {
  private readonly online = inject(OnlineWriter);
  private readonly store = inject(OfflineStore);
  private readonly engine = inject(SyncEngine);
  private readonly availability = inject(OfflineAvailabilityService);
  private readonly mode = inject(OfflineModeService);
  private readonly translate = inject(TranslateService);
  private readonly conflicts = inject(ConflictService);
  private readonly zone = inject(NgZone);
  private readonly auth = inject(AuthService);
  /** one write per task at a time (Revision 1, W4): key = local id if the task has one, else the server id */
  private readonly chains = new Map<string, Promise<unknown>>();
  /** keys resolve in call order (Revision 2, H2): two quick writes never swap places while their ids resolve */
  private keyOrder: Promise<unknown> = Promise.resolve();

  /** the outbox changed: views apply their local entries again, without a request */
  readonly changes$: Observable<void> = inAngularZone(this.zone, this.store.changes$);
  /** a task entry reached the server */
  readonly taskSynced$: Observable<SyncedEvent> = inAngularZone(this.zone, this.engine.synced$).pipe(
    filter((event) => event.type.startsWith('task.')),
  );
  /** a task created offline got its server id (no reload, spec §4.1) */
  readonly created$: Observable<TaskCreated> = this.taskSynced$.pipe(
    filter((event) => event.type === 'task.create' && !!event.localId && !!event.serverId),
    map((event) => ({ projectId: event.projectId, localId: event.localId!, serverId: event.serverId! })),
  );
  /** the server answers again: pages that show device data load from the server */
  readonly reachable$: Observable<void> = inAngularZone(this.zone, this.mode.online$);

  isOffline(): boolean {
    return this.mode.isOffline();
  }

  /** a read failed while connected (Funkloch): ask the server, so reachable$ fires once it answers again; true: it answers */
  checkReachable(): Promise<boolean> {
    return this.engine.checkReachable();
  }

  /** Shared-link guests (route, task of a shared project, no login) stay online-only (plan D13) */
  canWorkOffline(context: { isLoggedIn: boolean; sharedLinkId?: string | null; task?: { sharedLinkId?: unknown } | null }): boolean {
    return context.isLoggedIn && !context.sharedLinkId && !context.task?.sharedLinkId;
  }

  // ─── Reading ────────────────────────────────────────────────────────────────

  /** server: the API list (online), null: the snapshots of this device; projectId null = all projects */
  tasks$(projectId: string | null, server: Record<string, unknown>[] | null): Observable<TaskList> {
    return defer(() => this.loadTasks(projectId, server));
  }

  /** null: the task is neither in the server data, nor in a snapshot, nor in the outbox */
  task$(projectId: string | null, taskId: string, server: Record<string, unknown> | null): Observable<TaskLoad | null> {
    return defer(() => this.loadTask(projectId, taskId, server));
  }

  comments$(projectId: string | null, taskId: string, server: Record<string, unknown>[] | null): Observable<CommentList> {
    return defer(() => this.loadComments(projectId, taskId, server));
  }

  /** the static lists of the task form; projectId null: from any snapshot */
  entities$(projectId: string | null): Observable<TaskEntities | null> {
    return defer(async () => (await this.snapshots(projectId)).find((record) => record.data.entities)?.data.entities ?? null);
  }

  /** projects and journals for the Kanban filters while offline (GET /projects/filter is online-only) */
  filterInfo$(): Observable<TaskFilterInfo> {
    return defer(async () => {
      const records = await this.snapshots(null);
      return {
        projects: records.map((record) => ({ id: record.projectId, projectName: record.data.project?.projectName ?? record.name })),
        journals: records.flatMap((record) => this.journalViews(record)),
      };
    });
  }

  /** the task journals of a project on this device; null: the project is not on this device (journal lists, W1) */
  journals$(projectId: string, type?: string): Observable<TaskJournalView[] | null> {
    return defer(async () => {
      const [record] = await this.snapshots(projectId);
      if (!record) return null;
      return this.journalViews(record).filter((journal) => !type || journal.type === type);
    });
  }

  /** one task journal on this device, with the fixed BKP of a defect journal (Revision 1, FE-B1); projectId null: any */
  journal$(projectId: string | null, journalId: string): Observable<TaskJournalView | null> {
    return defer(async () =>
      (await this.snapshots(projectId)).flatMap((record) => this.journalViews(record)).find((journal) => journal.id === journalId) ?? null,
    );
  }

  /** server id of a task created offline once it is known (local: URL after the upload, step-2 parked point) */
  async serverIdFor(id: string): Promise<string | null> {
    return isLocalId(id) ? ((await this.store.resolveId(id)) ?? null) : id;
  }

  async photoBlob(blobId: string): Promise<Blob | undefined> {
    return (await this.store.getBlob(blobId))?.data;
  }

  /** after an online write: the snapshot must carry the new version, else the next offline edit conflicts falsely */
  noteOnlineChange(projectId: string): void {
    void this.availability.refresh(projectId, { fresh: true });
  }

  // ─── Writing ────────────────────────────────────────────────────────────────

  /** text first, then each photo (spec §6); online JSON create plus one upload per photo (plan D8) */
  async createTask(target: TaskTarget, fields: TaskCreateFields, photos: File[]): Promise<TaskWriteResult> {
    const label = taskLabel(fields.note);
    // E-4 follow-up: the capture time is the creation date too (the backend takes it only from the outbox, like
    // completionDate); one time for both, so «Erledigt» at creation is never before the creation (BE-W3)
    const capturedAt = new Date().toISOString();
    // a completionDate passed in by the caller is dropped like in updateTaskNow: only the capture time counts
    fields = { ...fields };
    delete fields.completionDate;
    fields = withCompletionDate({ ...fields, creationDate: capturedAt }, () => capturedAt);
    const localId = newLocalId();
    const ref = refOf(target.projectId, localId);
    const payload: TaskCreatePayload = {
      projectId: target.projectId,
      journalId: target.journalId,
      fields,
      journalName: target.journalName ?? null,
      journalType: target.journalType ?? null,
    };
    // the user who started the write owns it even if the account switches while the request is in flight
    const userId = this.store.currentUserId() ?? undefined;
    const input: EnqueueInput = { type: 'task.create', entityRef: ref, payload: payload as unknown as Record<string, unknown>, userId };

    if (!this.isOffline()) {
      const outcome = await this.online.send(input, TASK_SAVE_TIMEOUT_MS);
      if (outcome.kind === 'done') {
        const serverId = serverIdOf(outcome.body);
        if (!serverId) throw new Error('task.create answered without id');
        this.noteOnlineChange(target.projectId);
        const uploads = await this.uploadPhotos(target.projectId, serverId, photos, label, userId);
        return { taskId: serverId, ...uploads };
      }
      if (outcome.key) input.idempotencyKey = outcome.key;
    }
    const create = await this.store.enqueue(input);
    const rejectedPhotos = await this.queuePhotos(ref, localId, photos, [create.opId], label, undefined, userId);
    void this.engine.run();
    return { taskId: localId, queued: true, rejectedPhotos, failedPhotos: [] };
  }

  /** the fields of one action (Felder statt Diff); no field, no entry (BE Task 5 M2: a no-op would count the version) */
  updateTask(ref: TaskRef, fields: TaskFields): Promise<TaskWriteResult> {
    return this.serial(ref.taskId, () => this.updateTaskNow(ref, fields));
  }

  private async updateTaskNow(ref: TaskRef, fields: TaskFields): Promise<TaskWriteResult> {
    const entries = await this.store.listOutbox();
    const ids = await this.store.idMap();
    const taskId = ids.get(ref.taskId) ?? ref.taskId;
    // E2 gate R4: the device does not hold the note of this task (too long for the snapshot): the empty stand-in is
    // never sent as a change, and a note cannot be edited offline (the UI blocks it; this is the second line)
    if (fields.note !== undefined && (ref.noteTruncated === true || (this.isOffline() && (await this.loadTask(ref.projectId || null, taskId, null))?.view.noteTruncated))) {
      fields = { ...fields };
      delete fields.note;
    }
    if (Object.keys(fields).length === 0) return { taskId, queued: false, ...NO_PHOTOS };
    // a status action fixes its completion date now (E-4); only «Finished» carries one, never a value of the page
    fields = { ...fields };
    delete fields.completionDate;
    fields = withCompletionDate(fields);
    const label = ref.label ?? '';

    if (!this.mustQueue(taskId, entries, ids)) {
      // no ifVersion: the online body had none, and the same key needs the same body (C14)
      const input = this.updateInput(ref.projectId, taskId, fields, undefined, label);
      return { taskId, queued: await this.writeOnline(input, ref.projectId), ...NO_PHOTOS };
    }

    await this.queueUpdate(ref.projectId, taskId, fields, ref.version ?? undefined, entries, ids, label);
    void this.engine.run();
    return { taskId, queued: true, ...NO_PHOTOS };
  }

  /**
   * A task only on this device and never sent: dropped with its photos, comments and changes (dependsOn), no request.
   * Otherwise a delete chained explicitly only to the create of a task still in the outbox (Revision 2, H4): never to
   * an update, photo or comment, so discarding a refused one or «Neuere übernehmen» never takes the delete with it
   * (W5, H4). The implicit order per task keeps it after them. Online with key, 404 counts as done (plan D6).
   */
  deleteTask(ref: TaskRef): Promise<TaskDeleteResult> {
    return this.serial(ref.taskId, () => this.deleteTaskNow(ref));
  }

  private async deleteTaskNow(ref: TaskRef): Promise<TaskDeleteResult> {
    const entries = await this.store.listOutbox();
    const ids = await this.store.idMap();
    const taskId = ids.get(ref.taskId) ?? ref.taskId;
    const own = this.entriesOf(taskId, entries, ids);
    const create = own.find((entry) => entry.type === 'task.create');

    if (isLocalId(taskId) && create && (await this.lockForDiscard(create.opId, true))) {
      await this.engine.discard(create.opId);
      return { queued: false, discarded: true };
    }

    const input: EnqueueInput = {
      type: 'task.delete',
      entityRef: refOf(ref.projectId, taskId),
      payload: { taskId, label: ref.label ?? '' },
    };
    if (!this.mustQueue(taskId, entries, ids)) {
      // online without ifVersion: an interrupted delete keeps its key and request (C14)
      return { queued: await this.writeOnline(input, ref.projectId), discarded: false };
    }

    // E-3: the version the delete expects, like a follow-up update (version rule R1): after a queued update its
    // ifVersion + 1, after a sent local create 1, else the version of the page; unknown → no check (C14)
    const updates = own.filter((entry) => entry.type === 'task.update');
    const previous = updates.length ? (updates[updates.length - 1].payload as unknown as TaskUpdatePayload) : undefined;
    const ifVersion = previous ? (previous.ifVersion === undefined ? undefined : previous.ifVersion + 1) : create ? 1 : (ref.version ?? undefined);
    const payload = ifVersion === undefined ? input.payload : { ...input.payload, ifVersion };
    // a create still here (sent, not done): without it the task does not exist on the server (H4)
    await this.store.enqueue({ ...input, payload, dependsOn: create ? [create.opId] : [] });
    void this.engine.run();
    return { queued: true, discarded: false };
  }

  addPhotos(ref: TaskRef, photos: File[]): Promise<TaskWriteResult> {
    return this.serial(ref.taskId, () => this.addPhotosNow(ref, photos));
  }

  private async addPhotosNow(pageRef: TaskRef, photos: File[]): Promise<TaskWriteResult> {
    const userId = this.store.currentUserId() ?? undefined;
    const entries = await this.store.listOutbox();
    const ids = await this.store.idMap();
    const taskId = ids.get(pageRef.taskId) ?? pageRef.taskId;
    const ref = await this.completeRef(pageRef, taskId);
    const label = ref.label ?? '';
    if (!this.mustQueue(taskId, entries, ids)) {
      const uploads = await this.uploadPhotos(ref.projectId, taskId, photos, label, userId);
      if (photos.length > uploads.failedPhotos.length) this.noteOnlineChange(ref.projectId);
      return { taskId, ...uploads };
    }
    const create = this.entriesOf(taskId, entries, ids).find((entry) => entry.type === 'task.create');
    const rejectedPhotos = await this.queuePhotos(refOf(ref.projectId, taskId), taskId, photos, create ? [create.opId] : [], label, undefined, userId);
    void this.engine.run();
    return { taskId, queued: true, rejectedPhotos, failedPhotos: [] };
  }

  /** a comment (D4: offline only writing); on a local task it waits for the create */
  addComment(ref: TaskRef, text: string): Promise<TaskCommentResult> {
    // E-1: the backend refuses longer comments; queued, such an entry would only fail (the page shows a hint)
    if (text.length > COMMENT_MAX_LENGTH) return Promise.reject(new CommentTooLongError());
    return this.serial(ref.taskId, () => this.addCommentNow(ref, text));
  }

  private async addCommentNow(pageRef: TaskRef, text: string): Promise<TaskCommentResult> {
    const entries = await this.store.listOutbox();
    const ids = await this.store.idMap();
    const taskId = ids.get(pageRef.taskId) ?? pageRef.taskId;
    // the comments page may not know project and label (task load failed): from snapshot or outbox (final review M5)
    const ref = await this.completeRef(pageRef, taskId);
    const input: EnqueueInput = {
      type: 'task.comment',
      entityRef: refOf(ref.projectId, taskId),
      payload: { taskId, text, label: ref.label ?? '' },
    };
    if (!this.mustQueue(taskId, entries, ids)) {
      return { queued: await this.writeOnline(input, ref.projectId) };
    }
    const create = this.entriesOf(taskId, entries, ids).find((entry) => entry.type === 'task.create');
    await this.store.enqueue({ ...input, dependsOn: create ? [create.opId] : [] });
    void this.engine.run();
    return { queued: true };
  }

  /** a photo not uploaded yet (spec §6 «Foto einzeln entfernbar»); never once sent: it may be on the server (D9) */
  async removeQueuedPhoto(opId: string): Promise<boolean> {
    const entry = await this.store.getEntry(opId);
    if (entry?.type !== 'task.photo' || !(await this.lockForDiscard(opId, true))) return false;
    await this.engine.discard(opId);
    return true;
  }

  /** «Selbst zusammenführen» (plan D11): newer version, my fields and those of them that differ; null when solved */
  async loadMerge(opId: string): Promise<TaskMergeLoad | null> {
    const entry = await this.store.getEntry(opId);
    if (!entry || entry.type !== 'task.update' || entry.status !== 'conflict' || !entry.conflict) return null;
    const mine = (entry.payload as unknown as TaskUpdatePayload).fields;
    return {
      opId,
      current: toTaskView(entry.conflict),
      mine,
      diffs: taskConflictFields(mine, entry.conflict, this.translate).filter((field) => field.mine !== field.theirs),
    };
  }

  /**
   * Per field «Meine» or «Neuere»: my chosen values as a new update against the newer version; it replaces the
   * conflict entry (SyncEngine.replace: dependents now wait for it). Nothing left to send: «Neuere übernehmen», the
   * dependents stay (step-2 review I2). The sync is not awaited (step-2 review I1).
   */
  async saveMerged(opId: string, choices: TaskMergeChoices): Promise<void> {
    const merge = await this.loadMerge(opId);
    const entry = await this.store.getEntry(opId);
    if (!merge || !entry) return;
    const fields: TaskFields = {};
    for (const key of TASK_CONFLICT_KEYS) {
      if (!(key in merge.mine) || choices[key] !== 'mine') continue;
      if (JSON.stringify(merge.mine[key]) === JSON.stringify(merge.current[key])) continue;
      (fields as Record<string, unknown>)[key] = merge.mine[key];
    }
    if (Object.keys(fields).length === 0) {
      await this.conflicts.takeTheirs(opId, { keepDependents: true });
      return;
    }
    // my «Erledigt» keeps the time it was captured (E-4)
    if (fields.status === 'Finished' && merge.mine.completionDate) fields.completionDate = merge.mine.completionDate;
    const payload = entry.payload as unknown as TaskUpdatePayload;
    // follow-ups of the conflict entry move to the version after the merged update (before it is queued: Final-I2)
    await this.conflicts.rebaseAfterMerge(opId);
    const merged = await this.store.enqueue({
      type: 'task.update',
      entityRef: entry.entityRef,
      payload: { taskId: merge.current.id, ifVersion: merge.current.version, fields, label: payload.label ?? '' },
    });
    await this.engine.replace(opId, merged.opId);
    void this.engine.run();
  }

  // ─── Internals ──────────────────────────────────────────────────────────────

  /**
   * Runs the writes of one task one after the other (Revision 1, W4): two quick actions (Kanban dragged twice, status
   * and note in the details) read the outbox only after the first wrote its entry, so they merge or chain correctly.
   * Same tab only; other tabs stay as in step 2 (known). The key (an IndexedDB read for local ids) is resolved in call
   * order (H2), the work then waits only for writes of the same task.
   */
  private serial<T>(taskId: string, work: () => Promise<T>): Promise<T> {
    const registered = this.keyOrder.then(async () => ({ run: this.chain(await this.chainKey(taskId), work) }));
    this.keyOrder = registered.then(
      () => undefined,
      () => undefined,
    );
    return registered.then(({ run }) => run);
  }

  /** stable across the local to server id change: the local id whenever one exists, else the server id */
  private async chainKey(taskId: string): Promise<string> {
    if (isLocalId(taskId)) return taskId;
    for (const [localId, serverId] of await this.store.idMap()) if (serverId === taskId) return localId;
    return taskId;
  }

  private chain<T>(key: string, work: () => Promise<T>): Promise<T> {
    const previous = this.chains.get(key) ?? Promise.resolve();
    const run = previous.then(work);
    const settled = run.then(
      () => undefined,
      () => undefined,
    );
    this.chains.set(key, settled);
    void settled.then(() => {
      if (this.chains.get(key) === settled) this.chains.delete(key);
    });
    return run;
  }

  /** project and label of a task from snapshot or outbox when the page does not know them yet (FE-Review H3) */
  private async completeRef(ref: TaskRef, taskId: string): Promise<TaskRef> {
    if (ref.projectId && ref.label) return ref;
    const load = await this.loadTask(null, taskId, null);
    return {
      ...ref,
      projectId: ref.projectId || load?.view.projectId || '',
      label: ref.label || taskLabel(load?.view.note),
    };
  }

  private journalViews(record: SnapshotRecord<TaskSnapshotData>): TaskJournalView[] {
    return (record.data.taskJournals ?? []).map((journal) => ({
      id: journal.id,
      name: journal.name,
      type: journal.type,
      projectId: record.projectId,
      date: journal.date ?? null,
      bkp: journal.bkp ?? null,
      closedAt: journal.closedAt ?? null,
    }));
  }

  /** the outbox is the way: offline, a task only on this device, or waiting entries of this task (order stays) */
  private mustQueue(taskId: string, entries: OutboxEntry[], ids: Map<string, string>): boolean {
    if (this.isOffline()) return true;
    if (isLocalId(taskId)) return true;
    return this.entriesOf(taskId, entries, ids).length > 0;
  }

  private entriesOf(taskId: string, entries: OutboxEntry[], ids: Map<string, string>): OutboxEntry[] {
    const key = ids.get(taskId) ?? taskId;
    return entries.filter((entry) => entry.type.startsWith('task.') && taskKeyOf(entry, ids) === key);
  }

  private updateInput(projectId: string, taskId: string, fields: TaskFields, ifVersion: number | undefined, label: string): EnqueueInput {
    return {
      type: 'task.update',
      entityRef: refOf(projectId, taskId),
      payload: { taskId, ...(ifVersion === undefined ? {} : { ifVersion }), fields, label },
    };
  }

  /**
   * Merges into the unsent create of a local task (while no update follows, like reports B2) or into the last unsent
   * update; otherwise a follow-up with dependsOn on the last create/update of the task (explicit chain, Ruling Task 9)
   * and ifVersion + 1 (version rule R1). A local task starts with version 1.
   */
  private async queueUpdate(
    projectId: string,
    taskId: string,
    fields: TaskFields,
    baseVersion: number | undefined,
    entries: OutboxEntry[],
    ids: Map<string, string>,
    label: string,
  ): Promise<void> {
    const own = this.entriesOf(taskId, entries, ids);
    const create = own.find((entry) => entry.type === 'task.create');
    const updates = own.filter((entry) => entry.type === 'task.update');
    const last = updates[updates.length - 1];

    // a later status replaces the completion date of an earlier one (E-4): «Finished» keeps the new time, others drop it
    const merged = <T extends TaskFields>(current: T): T => withCompletionDate({ ...current, ...fields });
    if (create && !last) {
      const current = (create.payload as unknown as TaskCreatePayload).fields;
      if (await this.store.mergePayload(create.opId, { fields: merged(current) })) return;
    }
    if (last) {
      const current = (last.payload as unknown as TaskUpdatePayload).fields;
      if (await this.store.mergePayload(last.opId, { fields: merged(current), label })) return;
    }

    const previous = last ? (last.payload as unknown as TaskUpdatePayload) : undefined;
    // without a known version (an interrupted online save sends none) the follow-up checks none either (C14)
    const ifVersion = previous ? (previous.ifVersion === undefined ? undefined : previous.ifVersion + 1) : create ? 1 : baseVersion;
    const predecessor = last ?? create;
    await this.store.enqueue({
      ...this.updateInput(projectId, taskId, fields, ifVersion, label),
      dependsOn: predecessor ? [predecessor.opId] : [],
    });
  }

  /** one entry per photo; a full device returns this photo and the rest (spec §6: the task is already saved) */
  private async queuePhotos(ref: EntityRef, taskId: string, photos: File[], dependsOn: string[], label: string, firstKey?: string, userId?: string): Promise<File[]> {
    for (let index = 0; index < photos.length; index++) {
      const file = photos[index];
      try {
        await this.store.enqueue({
          type: 'task.photo',
          entityRef: ref,
          payload: { taskId, fileName: file.name, label },
          blobs: [file],
          dependsOn,
          userId,
          ...(index === 0 && firstKey ? { idempotencyKey: firstKey } : {}),
        });
      } catch (error) {
        if (error instanceof OfflineQuotaError) return photos.slice(index);
        throw error;
      }
    }
    return [];
  }

  /** online, one after the other; a cut off upload and the rest go into the outbox (same key for the cut off one) */
  private async uploadPhotos(
    projectId: string,
    taskId: string,
    photos: File[],
    label: string,
    /** the user who started the write (createTask, addPhotos), read once at its start (final review M3) */
    userId: string | undefined,
  ): Promise<Omit<TaskWriteResult, 'taskId'>> {
    const ref = refOf(projectId, taskId);
    const failedPhotos: FailedPhoto[] = [];
    for (let index = 0; index < photos.length; index++) {
      const file = photos[index];
      let outcome: OnlineOutcome;
      try {
        outcome = await this.online.send(
          { type: 'task.photo', entityRef: ref, payload: { taskId, fileName: file.name, label }, blobs: [file], userId },
          TASK_PHOTO_TIMEOUT_MS,
        );
      } catch (error) {
        failedPhotos.push({ file, message: syncErrorMessage(error) });
        continue;
      }
      if (outcome.kind === 'interrupted') {
        const rejectedPhotos = await this.queuePhotos(ref, taskId, photos.slice(index), [], label, outcome.key ?? undefined, userId);
        void this.engine.run();
        return { queued: true, rejectedPhotos, failedPhotos };
      }
    }
    return { queued: false, rejectedPhotos: [], failedPhotos };
  }

  /**
   * Sends one JSON write online. Done: noteOnlineChange, returns false. Cut off, 5xx or 429: the same body goes into the
   * outbox under the same key (spec §6), returns true. Other errors are thrown.
   */
  private async writeOnline(input: EnqueueInput, projectId: string): Promise<boolean> {
    const userId = this.store.currentUserId() ?? undefined;
    const outcome = await this.online.send(input, TASK_SAVE_TIMEOUT_MS);
    if (outcome.kind === 'done') {
      this.noteOnlineChange(projectId);
      return false;
    }
    await this.store.enqueue({ ...input, ...(outcome.key ? { idempotencyKey: outcome.key } : {}), userId });
    void this.engine.run();
    return true;
  }

  /** compare-and-set before discarding (step-2 review W2): never while it runs, and (neverSent) never once sent */
  private async lockForDiscard(opId: string, neverSent: boolean): Promise<boolean> {
    const locked = await this.store.updateEntry(opId, (entry) =>
      entry.status === 'running' || (neverSent && entry.sent) ? undefined : { status: 'failed', lastError: 'offline.errors.discarding' },
    );
    return !!locked;
  }

  private async loadTasks(projectId: string | null, server: Record<string, unknown>[] | null): Promise<TaskList> {
    const records = await this.snapshots(projectId);
    const base = server
      ? server.map(toTaskView)
      : records.flatMap((record) => (record.data.tasks ?? []).map((task) => fromSnapshotTask(task, record.projectId, record.data.project?.projectName ?? null)));
    const tasks = overlayTasks(base, await this.store.listOutbox(), await this.store.idMap(), this.context(records), projectId, await this.syncedOf(records, server));
    return { tasks, available: !!server || records.length > 0 };
  }

  private async loadTask(projectId: string | null, taskId: string, server: Record<string, unknown> | null): Promise<TaskLoad | null> {
    const ids = await this.store.idMap();
    const serverId = ids.get(taskId) ?? taskId;
    const records = await this.snapshots(projectId);
    const base = server
      ? [toTaskView(server)]
      : records.flatMap((record) =>
          (record.data.tasks ?? [])
            .filter((task) => task.id === serverId)
            .map((task) => fromSnapshotTask(task, record.projectId, record.data.project?.projectName ?? null)),
        );
    const view = overlayTasks(base, await this.store.listOutbox(), ids, this.context(records), null, await this.syncedOf(records, server)).find(
      (candidate) => candidate.id === serverId || candidate.localId === taskId,
    );
    if (!view) return null;
    return { view, server: base.find((candidate) => candidate.id === serverId) ?? null };
  }

  private async loadComments(projectId: string | null, taskId: string, server: Record<string, unknown>[] | null): Promise<CommentList> {
    const ids = await this.store.idMap();
    const serverId = ids.get(taskId) ?? taskId;
    const userId = this.store.currentUserId() ?? '';
    let olderOnlyOnline = false;
    let base = server ? server.map(toCommentView) : [];
    if (!server && !isLocalId(serverId)) {
      const record = (await this.snapshots(projectId)).find((candidate) => (candidate.data.tasks ?? []).some((task) => task.id === serverId));
      const data = record?.data;
      base = (data?.comments ?? [])
        .filter((comment) => comment.taskId === serverId)
        .map((comment) => fromSnapshotComment(comment, userId))
        .reverse();
      olderOnlyOnline = !!data && (!data.comments || !!data.commentLimits?.allTruncated || !!data.commentLimits?.truncatedTaskIds.includes(serverId));
    }
    const comments = overlayComments(serverId, base, await this.store.listOutbox(), ids, {
      userId,
      name: this.translate.instant('construction-diary.me'),
      organization: await this.ownCompany(projectId, userId),
    });
    return { comments, olderOnlyOnline };
  }

  /**
   * my company as the snapshot members know it (own comments offline, NA-02): the internal member row with my user id.
   * Without such a row (older snapshot without `userId`, external member) the row is found by first and last name
   * (trimmed, case-insensitive, guests and rows of another known user ignored) and only when exactly one member matches;
   * zero or several matches give no company, never a wrong one.
   */
  private async ownCompany(projectId: string | null, userId: string): Promise<string | null> {
    if (!userId) return null;
    const normalize = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();
    const firstName = normalize(this.auth.getFirstName());
    const lastName = normalize(this.auth.getLastName());
    for (const record of await this.snapshots(projectId)) {
      const members = (record.data.members ?? []).filter((member) => member.guest !== true);
      const mine = members.find((member) => member.userId === userId);
      if (mine) {
        if (mine.companyName) return mine.companyName;
        continue;
      }
      if (!firstName && !lastName) continue;
      const matches = members.filter(
        (member) => !member.userId && normalize(member.firstName) === firstName && normalize(member.lastName) === lastName,
      );
      if (matches.length === 1 && matches[0].companyName) return matches[0].companyName;
    }
    return null;
  }

  private async snapshots(projectId: string | null): Promise<SnapshotRecord<TaskSnapshotData>[]> {
    if (projectId) {
      const record = await this.store.getSnapshot<TaskSnapshotData>(projectId);
      return record ? [record] : [];
    }
    return (await this.store.listSnapshots()).filter((record) => record.kind === 'project') as SnapshotRecord<TaskSnapshotData>[];
  }

  /**
   * creates already synced that the base does not show yet (NA-03, FE-H10). Also over a server list: it may have been
   * loaded before the create went out (the network can drop right after the POST), and the card must not vanish until a
   * list or snapshot with the task arrives. overlayTasks dedupes by server id, so a list that has it shows one card.
   */
  private async syncedOf(records: SnapshotRecord<TaskSnapshotData>[], server: unknown): Promise<SyncedCreateRow[]> {
    if (server) return this.store.syncedCreates();
    const projects = new Set(records.map((record) => record.projectId));
    return (await this.store.syncedCreates()).filter((row) => projects.has(row.projectId));
  }

  private context(records: SnapshotRecord<TaskSnapshotData>[]): TaskOverlayContext {
    return {
      userId: this.store.currentUserId() ?? '',
      membersByProject: new Map(records.map((record) => [record.projectId, record.data.members ?? []])),
      projectNames: new Map(records.map((record) => [record.projectId, record.data.project?.projectName ?? record.name])),
    };
  }
}

export type { TaskView };
